import { FormEvent, useCallback, useEffect, useState } from "react";
import { commerceFetch } from "../lib/api";

type Merchant = { merchant_id: string; name: string; slug: string; status: string; shop_url: string };
type Sku = {
  sku_id: string; merchant_id: string; product_name: string; name: string;
  price_cents: number; stock_total: number; stock_remaining: number; stock_held: number;
  event_start_text: string | null; status: string; version: number;
};
type Grant = {
  grant_id: number; merchant_id: string; sales_org_id: string; sales_org_name: string;
  referrer_id: number | null; referrer_name: string | null; referrer_code: string | null;
  commission_pool_bps: number; attribution_policy: string; status: string; updated_at: number;
};
type Catalog = { merchants: Merchant[]; skus: Sku[]; grants: Grant[] };

const input = "w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100";
const label = "grid gap-1.5 text-xs font-semibold text-slate-400";
const td = "border-b border-slate-800 px-3 py-2.5 text-sm text-slate-300";
const th = "border-b border-slate-700 px-3 py-2 text-left text-xs font-semibold text-slate-400";
const money = (cents: number) => `HK$${(Number(cents || 0) / 100).toFixed(2)}`;

export default function CommercePage() {
  const [catalog, setCatalog] = useState<Catalog>({ merchants: [], skus: [], grants: [] });
  const [merchant, setMerchant] = useState("eid");
  const [org, setOrg] = useState("eid");
  const [referrer, setReferrer] = useState("");
  const [pool, setPool] = useState("2000");
  const [status, setStatus] = useState("active");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setBusy(true); setMessage("");
    try {
      const res = await commerceFetch("/api/commerce/admin/catalog");
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setCatalog({ merchants: body.merchants || [], skus: body.skus || [], grants: body.grants || [] });
    } catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function saveGrant(event: FormEvent) {
    event.preventDefault(); setBusy(true); setMessage("");
    try {
      const res = await commerceFetch(`/api/commerce/admin/grants?merchant=${encodeURIComponent(merchant)}`, {
        method: "POST",
        body: JSON.stringify({
          merchant_id: merchant, sales_org_id: org, referrer_id: referrer.trim() || null,
          commission_pool_bps: Number(pool), status,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      setMessage("品牌授權已保存；歷史訂單的佣金快照不受影響。");
      await load();
    } catch (error) { setMessage(error instanceof Error ? error.message : String(error)); }
    finally { setBusy(false); }
  }

  return <div className="space-y-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h1 className="text-2xl font-bold text-slate-100">多品牌銷售核心</h1><p className="mt-1 text-sm text-slate-400">商家、商品與收款各自獨立；同一團隊按品牌授權，永久從屬關係不重建。</p></div>
      <button onClick={load} disabled={busy} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800 disabled:opacity-50">重新載入</button>
    </div>
    {message && <div className="rounded-lg border border-emerald-800 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-300">{message}</div>}

    <section>
      <h2 className="mb-3 text-lg font-semibold text-slate-200">品牌與商品</h2>
      <div className="grid gap-3 md:grid-cols-2">
        {catalog.merchants.map(m => <div key={m.merchant_id} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="flex items-center justify-between gap-3"><b className="text-slate-100">{m.name}</b><span className="text-xs text-slate-400">{m.merchant_id} · {m.status}</span></div>
          <a href={m.shop_url} target="_blank" rel="noreferrer" className="mt-2 block break-all text-xs text-emerald-400">{m.shop_url}</a>
        </div>)}
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[900px] border-collapse"><thead><tr><th className={th}>SKU／產品</th><th className={th}>商家</th><th className={th}>價格</th><th className={th}>總名額</th><th className={th}>可售</th><th className={th}>留位</th><th className={th}>狀態</th></tr></thead>
          <tbody>{catalog.skus.map(s => <tr key={s.sku_id}><td className={td}><b>{s.sku_id}</b><br /><span className="text-xs text-slate-500">{s.product_name} · {s.name}</span></td><td className={td}>{s.merchant_id}</td><td className={td}>{money(s.price_cents)}</td><td className={td}>{s.stock_total}</td><td className={td}>{s.stock_remaining}</td><td className={td}>{s.stock_held}</td><td className={td}>{s.status} · v{s.version}</td></tr>)}</tbody></table>
      </div>
    </section>

    <section>
      <h2 className="mb-3 text-lg font-semibold text-slate-200">新增或更新品牌授權</h2>
      <form onSubmit={saveGrant} className="grid gap-4 rounded-xl border border-slate-800 bg-slate-900 p-5 md:grid-cols-5">
        <label className={label}>商家 ID<input className={input} value={merchant} onChange={e => setMerchant(e.target.value)} required /></label>
        <label className={label}>銷售組織 ID<input className={input} value={org} onChange={e => setOrg(e.target.value)} required /></label>
        <label className={label}>指定銷售 ID（可留空）<input className={input} value={referrer} onChange={e => setReferrer(e.target.value.replace(/\D/g, ""))} placeholder="空白＝整個團隊" /></label>
        <label className={label}>佣金池 bps<input className={input} type="number" min="0" max="10000" value={pool} onChange={e => setPool(e.target.value)} required /></label>
        <label className={label}>狀態<select className={input} value={status} onChange={e => setStatus(e.target.value)}><option value="active">生效</option><option value="suspended">停用</option></select></label>
        <div className="md:col-span-5 flex items-center gap-4"><button disabled={busy} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">保存授權</button><span className="text-xs text-slate-500">2000 bps＝20%；停用只影響新推廣，不改寫歷史。</span></div>
      </form>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-800"><table className="w-full min-w-[900px] border-collapse"><thead><tr><th className={th}>商家</th><th className={th}>銷售組織</th><th className={th}>指定銷售</th><th className={th}>佣金池</th><th className={th}>歸因</th><th className={th}>狀態</th><th className={th}>更新</th></tr></thead>
        <tbody>{catalog.grants.map(g => <tr key={g.grant_id}><td className={td}>{g.merchant_id}</td><td className={td}>{g.sales_org_id}<br /><span className="text-xs text-slate-500">{g.sales_org_name}</span></td><td className={td}>{g.referrer_id ? `${g.referrer_id} · ${g.referrer_name || ""} · ${g.referrer_code || ""}` : "整個團隊"}</td><td className={td}>{g.commission_pool_bps} bps（{g.commission_pool_bps / 100}%）</td><td className={td}>商家範圍首次有效推廣 30 日</td><td className={td}>{g.status}</td><td className={td}>{new Date(g.updated_at).toLocaleString("zh-HK")}</td></tr>)}</tbody></table></div>
    </section>
  </div>;
}
